import React from 'react';
import { cn } from '../../../utils/cn';
import { Thread } from '../types';

/**
 * In-app thread reader — reconstructs an email conversation from the extracted
 * timeline (date · sender · what they said), no Gmail needed. Ported from the
 * HTML report's openThread modal. Our-side senders render right-aligned.
 */

const OURS = /gangwal|kotnala|jain|infinitybox|swati|priyanka|asha|keshav|shashwat|harsh|jayanth|nilesh|jerry/i;

interface Props {
	thread: Thread | null;
	invoiceNum: string;
	customer?: string;
	onClose: () => void;
}

export const ThreadReader: React.FC<Props> = ({ thread, invoiceNum, customer, onClose }) => {
	if (!thread) return null;
	const tl = (thread.tl || []).filter(e => e.f || e.q);

	return (
		<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4' onClick={onClose}>
			<div
				className='flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl'
				onClick={e => e.stopPropagation()}
			>
				<div className='flex items-start justify-between border-b border-gray-200 p-4'>
					<div>
						<h2 className='text-base font-semibold text-gray-900'>
							{thread.subject || `${invoiceNum} · conversation`}
						</h2>
						<div className='mt-0.5 text-xs text-gray-500'>
							{invoiceNum} · {customer || ''} · {thread.msgs} messages
							{thread.acct ? ` · ${thread.acct}` : ''}
							{thread.link && (
								<>
									{' · '}
									<a
										href={thread.link}
										target='_blank'
										rel='noopener noreferrer'
										className='text-blue-600 hover:underline'
									>
										open in Gmail ↗
									</a>
								</>
							)}
						</div>
					</div>
					<button
						onClick={onClose}
						className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
						aria-label='Close'
					>
						✕
					</button>
				</div>

				<div className='flex-1 space-y-3 overflow-y-auto p-4'>
					<div className='rounded-lg bg-gray-50 p-2 text-[11px] text-gray-500'>
						Reconstructed from the extracted thread timeline (date · sender · what they said). Open in
						Gmail for the raw email.
					</div>
					{tl.length ? (
						tl.map((e, i) => {
							const mine = e.a ? OURS.test(e.a) : false;
							return (
								<div
									key={i}
									className={cn('flex', mine ? 'justify-end' : 'justify-start')}
								>
									<div
										className={cn(
											'max-w-[80%] rounded-lg p-3 text-sm',
											mine ? 'bg-blue-50 text-gray-800' : 'bg-gray-100 text-gray-800'
										)}
									>
										<div className='mb-1 flex items-center justify-between gap-3 text-[11px] text-gray-500'>
											<b className='text-gray-700'>{e.a || '—'}</b>
											<span>{e.d || ''}</span>
										</div>
										{e.f && <div className='text-gray-800'>{e.f}</div>}
										{e.q && (
											<div className='mt-1 border-l-2 border-gray-300 pl-2 text-xs italic text-gray-500'>
												&quot;{e.q}&quot;
											</div>
										)}
									</div>
								</div>
							);
						})
					) : (
						<div className='text-sm text-gray-400'>
							No per-message detail captured for this thread.
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
